/**
 * Path prefix under which a knowledge object's file is served.
 *
 * The backend route is `GET /api/v1/knowledge-objects/{sid}/file`. It resolves the object,
 * reads the asset from its content and delegates to the plain asset download, so both routes
 * serve identically.
 */
const KNOWLEDGE_OBJECT_PATH = '/api/v1/knowledge-objects';
const FILE_SEGMENT = 'file';

/** Path prefix under which a plain asset is served: `GET /api/v1/files/{uid}`. */
const ASSET_PATH = '/api/v1/files';

/**
 * Builds the src for a plain asset — the address a description stores for an image it uploaded.
 *
 * The uid is used EXACTLY as the upload returned it: it is a ShortId, and the route resolves that
 * spelling and no other. Named here rather than written out at each call site, because the same path
 * used to be spelled literally in more than one component and there is no compiler gate that would
 * notice the two drifting apart.
 */
export function buildAssetSrc(assetUid: string): string {
    return `${ASSET_PATH}/${encodeURIComponent(assetUid)}`;
}

/**
 * Builds the src a whiteboard or document stores for an embedded image.
 *
 * The URL addresses the KNOWLEDGE OBJECT, not the bytes. That is what keeps the embed
 * traceable: from an asset uid the object cannot be recovered, because ONE asset can back
 * SEVERAL image objects. Uploads are deduplicated by content hash, the objects around them are
 * not always — pasting now embeds the object that already holds the bytes, but the create
 * dialog still makes a new one for an asset that exists, and objects predating that reuse are
 * already duplicated. Two such objects would hold the identical URL, with no way to tell which
 * one a given embed belongs to.
 *
 * Addressing the object also means the URL survives if the object is later given other bytes.
 *
 * @param knowledgeObjectUid Uid of the image KnowledgeObject created for this embed.
 */
export function buildAssetEmbedSrc(knowledgeObjectUid: string): string {
    return `${KNOWLEDGE_OBJECT_PATH}/${encodeURIComponent(knowledgeObjectUid)}/${FILE_SEGMENT}`;
}

/**
 * Reads the knowledge object out of an embedded image's src, or null when the src does not
 * address one.
 *
 * Embeds written before this feature point at `/api/v1/files/<assetUid>` and yield null —
 * deliberately, since the object is not recoverable from them. They gain a reference when the
 * image is pasted again.
 */
export function knowledgeObjectUidFromEmbedSrc(src: string): string | null {
    const match = new RegExp(`^${KNOWLEDGE_OBJECT_PATH}/([^/]+)/${FILE_SEGMENT}$`).exec(src);

    return match ? decodeURIComponent(match[1]) : null;
}
